import * as React from 'react';
import { Input, FormField } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import Decimal from 'decimal.js';

interface TemplateFormValues {
  name?: string;
  chitValue?: number;
  subscriberCount?: number;
  durationCycles?: number;
  cycleFrequency?: string;
  commissionRatePct?: number;
  maxBidDiscountRatePct?: number;
  auctionType?: string;
  dividendCategory?: string;
  biddingWindowMinutes?: number;
  hasForemanTicket?: boolean;
  foremanTicketIsPaying?: boolean;
  fmPrDue?: number;
  requiresSurety?: boolean;
  maxSuretiesPerSubscriber?: number;
  tdsApplicable?: boolean;
  gstApplicable?: boolean;
}

interface TemplateFormFieldsProps {
  form: {
    state: { values: TemplateFormValues };
    Field: React.ComponentType<{
      name: string;
      validators?: Record<string, unknown>;
      children: (field: {
        name: string;
        state: { value: string | number | boolean | undefined; meta: unknown };
        handleBlur: () => void;
        handleChange: (value: unknown) => void;
      }) => React.ReactNode;
    }>;
  };
  fe: (meta: unknown) => string | undefined;
  isSubmitting: boolean;
  /** Validator carrying `.shape` entries for the rendered fields (a zod object's `.shape`). */
  validator: {
    shape: Record<string, { safeParse: (v: unknown) => { success: boolean; error?: { issues: { message: string }[] } } }>;
  };
}

function num(field: { state: { value: string | number | boolean | undefined } }, e: React.ChangeEvent<HTMLInputElement>): number {
  return new Decimal(e.target.value || 0).toNumber();
}

export function TemplateFormFields({ form, fe, isSubmitting, validator }: TemplateFormFieldsProps) {
  const v = validator.shape;

  const fieldValidators = (name: keyof TemplateFormValues & string) => ({
    onChange: ({ value }: { value: unknown }) =>
      v[name]?.safeParse(value).success ? undefined : v[name]?.safeParse(value).error?.issues[0]?.message,
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
      <div className="col-span-1 md:col-span-2 space-y-6">
        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Definition
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <form.Field
            name="name"
            validators={fieldValidators('name')}
            children={(field) => (
              <FormField label="Template Name" required error={fe(field.state.meta)}>
                <Input
                  name={field.name}
                  value={field.state.value as string}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g. Standard 20-Month Cash Chit"
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />

          <form.Field
            name="auctionType"
            validators={fieldValidators('auctionType')}
            children={(field) => (
              <FormSelect
                label="Auction Type"
                required
                name={field.name}
                value={field.state.value as string}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                options={[
                  { value: 'OPEN', label: 'Open Auction' },
                  { value: 'CLOSED', label: 'Closed (Sealed Bid)' },
                ]}
                error={fe(field.state.meta)}
                disabled={isSubmitting}
              />
            )}
          />
        </div>
      </div>

      <div className="col-span-1 space-y-6">
        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Financial Terms
        </h2>

        <form.Field
          name="chitValue"
          validators={fieldValidators('chitValue')}
          children={(field) => (
            <FormField label="Chit Value (INR)" required error={fe(field.state.meta)} hint="Entered in rupees; stored as paise">
              <Input
                type="number"
                min="1000"
                step="1000"
                name={field.name}
                value={field.state.value as number}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(num(field, e))}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="subscriberCount"
            validators={fieldValidators('subscriberCount')}
            children={(field) => (
              <FormField label="Members" required error={fe(field.state.meta)} hint="Between 5 and 50">
                <Input
                  type="number"
                  min="5"
                  max="50"
                  name={field.name}
                  value={field.state.value as number}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(num(field, e))}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />

          <form.Field
            name="durationCycles"
            validators={fieldValidators('durationCycles')}
            children={(field) => (
              <FormField label="Duration (Cycles)" required error={fe(field.state.meta)}>
                <Input
                  type="number"
                  min="1"
                  name={field.name}
                  value={field.state.value as number}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(num(field, e))}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
        </div>

        <form.Field
          name="cycleFrequency"
          validators={fieldValidators('cycleFrequency')}
          children={(field) => (
            <FormSelect
              label="Frequency"
              required
              name={field.name}
              value={field.state.value as string}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              options={[
                { value: 'MONTHLY', label: 'Monthly' },
                { value: 'FORTNIGHTLY', label: 'Fortnightly' },
                { value: 'WEEKLY', label: 'Weekly' },
              ]}
              error={fe(field.state.meta)}
              disabled={isSubmitting}
            />
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="commissionRatePct"
            validators={fieldValidators('commissionRatePct')}
            children={(field) => (
              <FormField label="Commission (%)" required error={fe(field.state.meta)} hint="Max 7% (statutory ceiling)">
                <Input
                  type="number"
                  min="0"
                  max="7"
                  step="0.01"
                  name={field.name}
                  value={field.state.value as number}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(num(field, e))}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />

          <form.Field
            name="maxBidDiscountRatePct"
            validators={fieldValidators('maxBidDiscountRatePct')}
            children={(field) => (
              <FormField label="Max Bid Discount (%)" error={fe(field.state.meta)} hint="Max 40% (statutory ceiling)">
                <Input
                  type="number"
                  min="0"
                  max="40"
                  step="0.01"
                  name={field.name}
                  value={field.state.value as number}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(num(field, e))}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
        </div>
      </div>

      <div className="col-span-1 space-y-6">
        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Foreman Ticket &amp; Settlement
        </h2>

        <form.Field
          name="hasForemanTicket"
          children={(field) => (
            <div className="flex items-center gap-2">
              <Input
                type="checkbox"
                checked={field.state.value as unknown as boolean}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-accent-500 w-4 h-4"
                disabled={isSubmitting}
              />
              <label className="text-sm font-medium text-text-primary">Has Foreman Ticket (ticket 1 reserved for the company)</label>
            </div>
          )}
        />

        {form.state.values.hasForemanTicket && (
          <>
            <form.Field
              name="foremanTicketIsPaying"
              children={(field) => (
                <div className="flex items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={field.state.value as unknown as boolean}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    className="accent-accent-500 w-4 h-4"
                    disabled={isSubmitting}
                  />
                  <label className="text-sm font-medium text-text-primary">Foreman ticket carries a payment obligation</label>
                </div>
              )}
            />

            {form.state.values.foremanTicketIsPaying && (
              <form.Field
                name="fmPrDue"
                children={(field) => (
                  <FormField label="Foreman PR Due" error={fe(field.state.meta)}>
                    <Input
                      type="number"
                      min="0"
                      name={field.name}
                      value={field.state.value as number}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(num(field, e))}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            )}
          </>
        )}

        <form.Field
          name="requiresSurety"
          children={(field) => (
            <div className="flex items-center gap-2">
              <Input
                type="checkbox"
                checked={field.state.value as unknown as boolean}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-accent-500 w-4 h-4"
                disabled={isSubmitting}
              />
              <label className="text-sm font-medium text-text-primary">Prize payouts require surety</label>
            </div>
          )}
        />

        {form.state.values.requiresSurety && (
          <form.Field
            name="maxSuretiesPerSubscriber"
            validators={fieldValidators('maxSuretiesPerSubscriber')}
            children={(field) => (
              <FormField label="Max Sureties per Subscriber" error={fe(field.state.meta)} hint="Between 0 and 5">
                <Input
                  type="number"
                  min="0"
                  max="5"
                  name={field.name}
                  value={field.state.value as number}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(num(field, e))}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
        )}

        <div className="flex gap-6 pt-2">
          <form.Field
            name="tdsApplicable"
            children={(field) => (
              <div className="flex items-center gap-2">
                <Input
                  type="checkbox"
                  checked={field.state.value as unknown as boolean}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.checked)}
                  className="accent-accent-500 w-4 h-4"
                  disabled={isSubmitting}
                />
                <label className="text-sm font-medium text-text-primary">TDS Applicable</label>
              </div>
            )}
          />

          <form.Field
            name="gstApplicable"
            children={(field) => (
              <div className="flex items-center gap-2">
                <Input
                  type="checkbox"
                  checked={field.state.value as unknown as boolean}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.checked)}
                  className="accent-accent-500 w-4 h-4"
                  disabled={isSubmitting}
                />
                <label className="text-sm font-medium text-text-primary">GST Applicable</label>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}
